/**
 * `usePendingInvites` — live subscription to
 * `organizations/{orgId}/pendingInvites/{phone}`. These are phone-
 * keyed invites for users who haven't signed in yet; once they sign
 * in, `claimInvites` moves them into `organizations/{orgId}.memberIds`
 * and removes the pending row.
 *
 * Mirrors the mobile hook at
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/org/usePendingInvites.ts
 *
 * The Team slide-over filters this list to invites where
 * `projectIds.includes(currentProjectId)`.
 */
import { collection, onSnapshot } from 'firebase/firestore';
import { useEffect, useState } from 'react';

import { db } from '@/lib/firebase';
import type { RoleKey } from '@/lib/useProjectMembers';

export type PendingInvite = {
  /** E.164 phone number — also the doc id. */
  phoneNumber: string;
  role: RoleKey;
  displayName: string | null;
  projectIds: string[];
  /** Legacy single-project field still read for back-compat. */
  projectId: string | null;
  invitedBy: string | null;
  invitedAtMs: number | null;
};

export type UsePendingInvitesResult = {
  invites: PendingInvite[];
  loading: boolean;
};

export function usePendingInvites(
  orgId: string | null | undefined,
): UsePendingInvitesResult {
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) {
      setInvites([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      collection(db, 'organizations', orgId, 'pendingInvites'),
      (snap) => {
        const out: PendingInvite[] = [];
        snap.forEach((d) => {
          const v = d.data() as Record<string, unknown>;
          const legacyProjectId =
            typeof v.projectId === 'string' ? v.projectId : null;
          const projectIds = Array.isArray(v.projectIds)
            ? (v.projectIds.filter((p) => typeof p === 'string') as string[])
            : legacyProjectId
              ? [legacyProjectId]
              : [];
          out.push({
            phoneNumber: (v.phoneNumber as string) ?? d.id,
            role: v.role as RoleKey,
            displayName:
              typeof v.displayName === 'string' && v.displayName
                ? v.displayName
                : null,
            projectIds,
            projectId: legacyProjectId,
            invitedBy: typeof v.invitedBy === 'string' ? v.invitedBy : null,
            invitedAtMs:
              v.invitedAt &&
              typeof (v.invitedAt as { toMillis?: () => number }).toMillis ===
                'function'
                ? (v.invitedAt as { toMillis: () => number }).toMillis()
                : null,
          });
        });
        setInvites(out);
        setLoading(false);
      },
      (err) => {
        console.warn('[usePendingInvites] snapshot error:', err);
        setLoading(false);
      },
    );
  }, [orgId]);

  return { invites, loading };
}
